import { Plugin } from "@ckeditor/ckeditor5-core";
import {
  toWidget,
  viewToModelPositionOutsideModelElement,
} from "@ckeditor/ckeditor5-widget/src/utils";
import { Widget } from "@ckeditor/ckeditor5-widget";

import CreateInteractionCommand from "./CreateInteractionCommand";
import interactionTypes from "./interactionTypes.json";

export default class InteractionEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  destroy() {
    console.debug(
      "Removing listeners for cde:createInteraction && cde:insertInteractionElements",
    );
    this.editor.off("cde:createInteraction");
    this.editor.off("cde:insertInteractionElements");
  }

  init() {
    const editor = this.editor;
    const model = editor.model;
    this._defineSchema();
    this._defineConverters();

    editor.commands.add(
      "createInteraction",
      new CreateInteractionCommand(editor, true),
    );

    if (editor.plugins.has("TrackChangesEditing")) {
      const trackChangesEditing = editor.plugins.get("TrackChangesEditing");
      trackChangesEditing.enableCommand("createInteraction");
    }

    // Listen for the component to tell us to insert interaction elements
    editor.on("cde:insertInteractionElements", (_eventInfo, ...args) => {
      const { elements, interaction, type } = args[0];
      model.change((writer) => {
        let blockInteractionElement;
        let inlineInteractionElement;
        console.debug(`Creating ${type} interaction: %o`, interaction);
        // eslint-disable-next-line default-case
        switch (type) {
          case "Choice":
          case "Extended Text":
          case "Graphic Gap Match":
          case "Match":
          case "Match Table":
          case "Order":
          case "Hotspot":
            blockInteractionElement = writer.createElement(
              "blockInteractionElement",
              { name: interaction.identifier, type },
            );
            model.insertContent(blockInteractionElement);
            break;
          case "Gap Match":
            elements.map((element) => {
              if (element === "Elements") {
                blockInteractionElement = writer.createElement(
                  "blockInteractionElement",
                  { element, name: interaction.identifier, type },
                );
                writer.append(
                  blockInteractionElement,
                  model.document.getRoot(),
                );
              } else {
                inlineInteractionElement = writer.createElement(
                  "inlineInteractionElement",
                  { element, name: interaction.identifier, type },
                );
                const allowed = model.schema.findAllowedParent(
                  model.document.selection.getFirstPosition(),
                  "inlineInteractionElement",
                );
                if (allowed) {
                  model.insertContent(inlineInteractionElement);
                } else {
                  const paragraph = writer.createElement("paragraph");
                  writer.append(inlineInteractionElement, paragraph);
                  writer.append(paragraph, editor.model.document.getRoot());
                }
              }
            });
            break;
          case "Inline Choice":
          case "Demographic Text Entry":
          case "Text Entry":
            inlineInteractionElement = writer.createElement(
              "inlineInteractionElement",
              { name: interaction.identifier, type },
            );
            model.insertContent(inlineInteractionElement);
            break;
        }
      });
    });

    editor.editing.mapper.on(
      "viewToModelPosition",
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) =>
        viewElement.hasClass("interaction"),
      ),
    );
  }

  _defineConverters() {
    const editor = this.editor;
    const conversion = editor.conversion;

    const createInteractionModelElement =
      (elementName) =>
      (viewElement, { writer: modelWriter }) => {
        const element = viewElement.getAttribute("data-interaction-element");
        const name = viewElement.getAttribute("data-interaction-name");
        const type = viewElement.getAttribute("data-interaction-type");

        const params = { element, name, type };
        return modelWriter.createElement(
          elementName,
          Object.keys(params).reduce(
            (acc, key) =>
              params[key] === undefined
                ? { ...acc }
                : { ...acc, [key]: params[key] },
            {},
          ),
        );
      };

    const createInteractionViewElement =
      (elementName) => (modelElement, viewWriter) => {
        const element = modelElement.getAttribute("element");
        const name = modelElement.getAttribute("name");
        const type = modelElement.getAttribute("type");
        const label = interactionTypes.find((o) => o.type === type).label;

        const params = {
          class: "interaction",
          "data-interaction-element": element,
          "data-interaction-name": name,
          "data-interaction-type": type,
        };
        const interactionViewElement = viewWriter.createContainerElement(
          elementName,
          Object.keys(params).reduce(
            (acc, key) =>
              params[key] === undefined
                ? { ...acc }
                : { ...acc, [key]: params[key] },
            {},
          ),
        );

        const content = element
          ? viewWriter.createText(`${label} (${name}) - ${element}`)
          : viewWriter.createText(`${label} (${name})`);
        viewWriter.insert(
          viewWriter.createPositionAt(interactionViewElement, 0),
          content,
        );

        return interactionViewElement;
      };

    const createBlockInteractionView = createInteractionViewElement("section");

    const createInlineInteractionView = createInteractionViewElement("slot");

    // Block Interaction Element conversions
    conversion.for("dataDowncast").elementToElement({
      model: "blockInteractionElement",
      view: (modelElement, { writer: viewWriter }) => {
        return createBlockInteractionView(modelElement, viewWriter);
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "blockInteractionElement",
      view: (modelElement, { writer: viewWriter }) => {
        const section = createBlockInteractionView(modelElement, viewWriter);
        return toWidget(section, viewWriter);
      },
    });
    conversion.for("upcast").elementToElement({
      model: createInteractionModelElement("blockInteractionElement"),
      view: {
        classes: ["interaction"],
        name: "section",
      },
    });

    // Inline Interaction Element conversions
    conversion.for("dataDowncast").elementToElement({
      model: "inlineInteractionElement",
      view: (modelElement, { writer: viewWriter }) => {
        return createInlineInteractionView(modelElement, viewWriter);
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: "inlineInteractionElement",
      view: (modelElement, { writer: viewWriter }) => {
        const slot = createInlineInteractionView(modelElement, viewWriter);
        return toWidget(slot, viewWriter);
      },
    });
    conversion.for("upcast").elementToElement({
      model: createInteractionModelElement("inlineInteractionElement"),
      view: {
        classes: ["interaction"],
        name: "slot",
      },
    });
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("blockInteractionElement", {
      allowAttributes: ["name", "type"],
      allowContentOf: "$text",
      allowWhere: "$block",
      isObject: true,
    });

    schema.register("inlineInteractionElement", {
      allowAttributes: ["element", "name", "type"],
      allowContentOf: "$text",
      allowWhere: "$text",
      isObject: true,
    });
  }
}
